import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {Observable, of as observableOf} from 'rxjs';
import {DialogData, DialogValidationComponent} from '@Common/widgets/general/dialog-validation';
import {catchError, debounceTime, distinctUntilChanged, filter, switchMap} from 'rxjs/operators';

import { MatDialog } from '@angular/material/dialog';

import {DaoGeneric} from '@Services/base/daoService/generic.dao';
import {actionEmitted} from '@Common/widgets/general/select2/select2.interface';
import {actionSelectEnum} from '@Common/widgets/general/select2/select2-actions.enum';

/**
 * Component "select2"
 *
 * Recherche et sélection d'éléments sous forme de "chips"
 */
@Component({
  selector: 'app-select2',
  templateUrl: './select2.component.html',
  styleUrls: ['./select2.component.scss'],
})
export class Select2Component<T> implements OnInit, OnChanges {
  @Input() select2Title = 'Matériel';
  @Input() select2Description = '';
  @Input() searchPlaceHolder = 'Rechercher';
  @Input() toolTip = 'Rechercher';
  @Input() removeElementMessageTooltip = '';
  @Input() confirmDeleteTitle = '';
  @Input() confirmDeleteMessage = 'Confirmez-vous le détachement ?';
  @Input() confirmModale = true;
  /**
   * Clé (id) de T à utiliser pour la comparaison des objets ramenés de T
   */
  @Input() keyItem = 'uid';
  /**
   * Limite des items / tags possibles
   * 0 : non limité
   * N : limit à N
   */
  @Input() limitSelectedItems = 0;
  /**
   * étendre le panel à l'ouverture du component ?
   */
  @Input() expanded = false;
  /**
   *  La recherche se déclenche à partir de miniCaracteresSearch caractères
   */
  @Input() miniCaracteresSearch = 2;

  /**
   * propriété(s) de l'item à aller chercher pour afficher les propositions dans la liste résultats de recherche
   * l'affichage est la concaténation des valeurs trouvées
   * par défaut label
   */
  @Input() fieldsDisplay: string[] = ['label'];

  /**
   * valeurs par défaut à afficher sous forme de liste de Chips
   */
  @Input() values: T[] = null;

  /**
   * Action opérée sur un élément : selection d'un nouveau, suppression d'un de la liste
   */
  @Output() actionItem: EventEmitter<actionEmitted<T>> = new EventEmitter();

  /**
   * search input
   */
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  private _daoService: DaoGeneric<T>;
  private _funSearch: (string) => Observable<T[]>;
  private _argFunSearch;
  /**
   * search control
   */
  ItemControl = new UntypedFormControl();
  itemSearch: Observable<any>;
  items: T[] = [];
  searchTerm = '';
  /**
   * Réinitialisation du champ recherche
   */
  public resetSearch() {
    this._attachSearch();
  }
  /**
   * Dao service pour la recherche, par défaut il appelera la fonction 'search' du service
   */
  set daoService(value: DaoGeneric<T>) {
    this._daoService = value;
  }
  /**
   * La fonction de recherche de DaoGeneric, par défaut "search"
   */
  set funSearch(value: (string) => Observable<T[]>) {
    this._funSearch = value;
  }
  /**
   * Argument supplémentaire pour l'appel de funSearch, optionnel
   * @param {any | any[]} value : une valeur ou un tableau de valeurs à ajouter à la fonction, chaque valeur du tableau correspond à chaque argument de la fonction funSearch
   * @example 'toto', ['toto', 'tata'], ...
   */
  set argFunSearch(value: any | any[]) {
    this._argFunSearch = value;
  }
  constructor(public dialog: MatDialog) {}

  ngOnInit() {
    this._attachSearch();
  }

  /**
   * init chips avec les valeurs passées
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.values && changes.values.currentValue !== null) {
      this._makeItemsChips(changes.values.currentValue);
    }
    /*if (changes.values && changes.values.currentValue !== null ||
       (changes.values.currentValue !== changes.values.previousValue)) {
      this._makeItemsChips(changes.values.currentValue);
    }*/
  }

  /**
   * valeur à afficher dans la liste / propositions de recherche
   * par défaut : le champ doit contenir un champ 'label'
   * @param item
   */
  getFieldsItemToDisplay(item) {
    if (this.fieldsDisplay && this.fieldsDisplay.length > 0) {
      return this.fieldsDisplay.reduce(function(acc, currentValue, idx) {
        return idx === 0 ? item[currentValue] : `${acc} ${item[currentValue]}`;
      }, '');
    }

    return item['label'];
  }

  /**
   * Peut-on ajouter plus d'un élément ?
   */
  canAddMoreElement() {
    return this.limitSelectedItems <= 0 ||
      (this.limitSelectedItems > 0 && this.items.length < this.limitSelectedItems);
  }

  /**
   * ajout d'un item à la liste de chips
   * @param item T
   * @param attach appel d'attachSearch() ?
   */
  addItem(item: T, attach = true) {
    let eventFired = false;

    if (this.canAddMoreElement()) {
      eventFired = this._addItem(item);
    }

    if (eventFired) {
      const added: actionEmitted<T> = {
        action: actionSelectEnum.added,
        itemUpdated: item,
        itemsList: this.items
      };
      this.actionItem.emit(added);
    }

    if (attach) {
      this._attachSearch();
    }
  }
  /**
   * Suppression d'un élément de la liste d'éléments
   * @param item
   */
  removeItem(item) {
    const find = this.items.findIndex( (g: any) => {
      if (this.keyItem in g) {
        return g[this.keyItem] === item[this.keyItem];
      }
      return false;
    });
    if (find > -1) {
      this.items.splice(find, 1);
      const del: actionEmitted<T> = {
        action: actionSelectEnum.deleted,
        itemUpdated: item,
        itemsList: this.items
      };

      this.actionItem.emit(del);
    }
  }
  /**
   * Calcul de la fonction dynamique et de ses arguments
   * @param val
   */
  private _getCallfunArg(val) {
    let callFun;
    if (this._argFunSearch !== null) {
      callFun = Array.isArray(this._argFunSearch) ?
        this._funSearch.call(this._daoService, val, ...this._argFunSearch) :
        this._funSearch.call(this._daoService, val, this._argFunSearch);
    } else {
      callFun = this._funSearch.call(this._daoService, val);
    }
    return callFun;

  }
  /**
   * Attachement champ de recherche
   * TODO : voir à faire un of('')  pour précharger une liste (optionnellement) avec un merge
   *  merge(this.ItemControl.valueChanges, of('')).pipe() ou un startWith('')
   *  le filter(minCaracteresSearch) est à enlever dans ce cas
   */
  private _attachSearch() {
    if (this.searchInput) {
      this.searchInput.nativeElement.value = '';
      this.itemSearch = this.ItemControl.valueChanges
        .pipe(
          // startWith(''),
          debounceTime(250),
          distinctUntilChanged(),
          filter((searchTerm) => searchTerm.length >= this.miniCaracteresSearch),
          switchMap(val => {
            if (this._funSearch) {
              return this._getCallfunArg(val);
            }
            return this._daoService.search(val);
          }),
          catchError(err => {
              return  observableOf([]);
            }
          ));
    }
  }
  /**
   * Valeurs par défaut à afficher dans la liste de chips
   * @param items
   */
  private _makeItemsChips(items: T[]) {
    if (items) {
      for (let i = 0; i < items.length; i++) {
        this.addItem(items[i], false);
      }
    }
  }

  /**
   * ajout d'un élément dans la liste des éléments
   * @param item
   * @private
   */
  private _addItem(item) {
    const find = this.items.find( (g) => {
      return g[this.keyItem] === item[this.keyItem];
    });

    if (! find) {
      this.items.push(item);
      return true;
    }

    return false;
  }

  /**
   * Confirmation de suppression d'un élément de la liste
   * @param item
   */
  deleteConfirmItemDialog(item: T) {
    if (this.confirmModale) {
      const data = new DialogData();
      data.title = this.confirmDeleteTitle;
      data.message = `${this.confirmDeleteMessage} (${this.getFieldsItemToDisplay(item)})`;

      const dialogRef = this.dialog.open(DialogValidationComponent, {data: data});
      dialogRef.updatePosition({top: '40px'});
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.removeItem(item);
        }
      });
    } else {
      this.removeItem(item);
    }
  }
}
